<template>
  <div :class="['activity-package', {'selected': isSelectedPackage}]">
    <div class="activity-package--head" @click="openPackage">
      <div class="activity-package--head-title">
        <RadioButton v-model="propertiesStore.selectedPackageId" :value="package.id"/>
        <div class="activity-package--title">
          {{package.title}}
        </div>
      </div>
      <div>
        <div v-if="price">from <strong>{{ price }}</strong></div>
      </div>
    </div>
    <div v-show="isSelectedPackage">
      <div class="package-section">
        <div class="package-section-info">
          <div class="package-section-info__item" v-if="property.packages_depend_on_pax_amount">
            <span>Price varies by group size</span>
          </div>
          <div class="package-section-info__item package-cancellation" v-if="hasFreeCancellation">
            <img src="/images/check.svg" alt="">
            <span>Free cancellation available</span>
          </div>
        </div>
        <!-- Entrance tickets and other all-day products carry a single placeholder slot at
             00:00:00. Bucketing that by hour lands it in "Night" and prints "00:00:00" as a
             time to pick — both are fiction, and the product is valid for the whole day. -->
        <div v-if="!isTimeless">
          <div class="package-grouped-times">
            <div
              :class="['package-grouped-times__item', {active: selectedDateTime === 'morning', disabled: !groupedTimes.morning.length}]"
              @click="selectDateTime('morning', !groupedTimes.morning.length)"
            >
              Morning
            </div>
            <div
              :class="['package-grouped-times__item', {active: selectedDateTime === 'afternoon', disabled: !groupedTimes.afternoon.length}]"
              @click="selectDateTime('afternoon', !groupedTimes.afternoon.length)"
            >
              Afternoon
            </div>
            <div
              :class="['package-grouped-times__item', {active: selectedDateTime === 'evening', disabled: !groupedTimes.evening.length}]"
              @click="selectDateTime('evening', !groupedTimes.evening.length)"
            >
              Evening
            </div>
            <div
              :class="['package-grouped-times__item', {active: selectedDateTime === 'night', disabled: !groupedTimes.night.length}]"
              @click="selectDateTime('night', !groupedTimes.night.length)"
            >
              Night
            </div>
        </div>

        <div v-if="selectedDateTime" class="package-times-list">
          <div
            v-for="time in groupedTimes[selectedDateTime]"
            @click="updatePackageSlot(time)"
            :class="['package-times-list__item', {active: selectedTimeSlot?.id === time.value}]"
          >
            <span>{{formatSlotTime(time.label)}}</span>
            </div>
          </div>
        </div>
        <div class="availability-selector" v-show="ticketsList.length > 0">
          <div v-for="ticket in ticketsList" :key="ticket.id" class="availability-selector__item">
            <div class="availability-selector__item-info">
              <span class="text-body-1">{{ticket.title}}</span> -
              <span class="ticket-price">
            <span v-if="ticket.price.amount === '0.00'">Free</span>
            <span v-else>{{ticket.price.formatted}}</span>
          </span>
            </div>
            <div class="availability-quantity">
              <Button
                  rounded
                  class="prima"
                  aria-label="Less"
                  size="small"
                  :disabled="!selectedHolders[ticket.holder_type]"
                  @click="() => {updateAvailability(ticket.holder_type, -1, ticket.max_units)}"
              >
                <i class="icon icon-minus"></i>
              </Button>
              <div class="availability-quantity__value text-body-1">
                {{selectedHolders[ticket.holder_type]}}
              </div>
              <Button
                  rounded
                  aria-label="More"
                  size="small"
                  @click="() => {updateAvailability(ticket.holder_type, 1, ticket.max_units)}"
              >
                <i class="icon icon-plus"></i>
              </Button>
            </div>
          </div>
        </div>
        <div class="activity-package-footer">
          <div class="activity-package-error" v-if="noAvailablePackage">
            <span class="error-message">No available packages for this selection</span>
          </div>
          <div class="activity-package-error" v-else-if="bookingError">
            <span class="error-message">{{ bookingError }}</span>
          </div>
          <div class="package-summery">
            <div>
              <div class="package-summery--total">Total {{currency.symbol}}{{ totalPrice }}</div>
              <div class="package-summery--tax">All taxes and fees included</div>
            </div>
            <Button
                class="btn btn-primary checkout-button"
                :class="{'is-pending': isBooking}"
                :disabled="!selectedQuantity || !selectedActivityDate || isPending || isBooking || noAvailablePackage"
                :aria-busy="isBooking"
                @click="bookNow">
              <template v-if="isBooking">
                <span class="btn-spinner" aria-hidden="true"/> Taking you to checkout...
              </template>
              <template v-else>
                Book now <span class="icon icon-chevron-right"/>
              </template>
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import RadioButton from 'primevue/radiobutton';
import Button from 'primevue/button'
import { useSearchStore } from "~/store/search";
import { useActivityPropertyStore } from "~/store/activityProperty";
import { useBookingStore } from "~/store/booking";
import { useAccountStore } from "~/store/account";
import dayjs from '~/utils/dayjs'
import { useRouter } from "vue-router";

const accountStore = useAccountStore();
const localePath = useLocalePath()
const router = useRouter();
const searchStore = useSearchStore();
const propertiesStore = useActivityPropertyStore();
const bookingStore = useBookingStore();
const props = defineProps({package: {type: Object, required: true}})
const availabilitiesList = ref<any>([])
const slot = ref<any>(null)
const selectedTimeSlot = ref<any>(null)
const ticketsList = ref<any[]>([])
// isPending also covers the quantity -> re-price round trip, so it must NOT drive the checkout
// label: changing quantity briefly flashed "Taking you to checkout...". Booking gets its own flag.
const isPending = ref(false)
const isBooking = ref(false)
const bookingError = ref('')
let selectedHolders = ref<any>({})
const { gtag } = useGtag()
const totalPrice = ref(0)
// The button used to be gated on `totalPrice`, which was wrong in both directions: a genuinely
// free ticket (the row renders "Free") never moved it off 0, so those products could not be
// booked at all; and `updatePackageSlot` resets the quantities without resetting the price, so a
// stale total left the button live with nothing selected. Clicking then posted an empty
// `availabilities` array and order-api answered 422 — 34% of Book clicks on 2026-09-02. Count
// what was actually selected instead.
const selectedQuantity = computed(() =>
  Object.values(selectedHolders.value).reduce((sum: number, n: any) => sum + (Number(n) || 0), 0)
)
const selectedDateTime = ref('')
const noAvailablePackage = ref(false)
const property = computed(() => propertiesStore.property);

const groupedTimes = reactive<any>({
  morning: [],
  afternoon: [],
  evening: [],
  night: []
})

const selectedActivityDate = computed(() => searchStore.activitySearchDate);
const isSelectedPackage = computed(() => propertiesStore.selectedPackageId === props.package.id);

// Musement returns 'HH:MM' for some products and 'HH:MM:SS' for others; midnight is its
// placeholder for "no particular time", not an actual midnight departure.
const isPlaceholderTime = (value: any) => !value || /^00:00(:00)?$/.test(String(value))
const formatSlotTime = (value: any) => String(value ?? '').slice(0, 5)
const isTimeless = computed(() => {
  const slots = props.package?.time_slots || []
  return slots.length > 0 && slots.every((slot: any) => isPlaceholderTime(slot.time))
})

const currency = computed(() => accountStore.currency)
// Musement labels the full-fare ticket 'adult' on some products and 'standard-price' on
// others — a tour can have no 'adult' type at all. Substring match on the same hints ETL
// uses (etl/libs/activity.py:25) so the package price agrees with the headline "Price from".
const ADULT_HOLDER_HINTS = ['adult', 'standard']
const isAdultHolder = (holderType: any) => {
  const code = String(holderType).toLowerCase()
  return ADULT_HOLDER_HINTS.some((hint) => code.includes(hint))
}
// "from 0" had two causes: the old test was === 'ADULT' but Musement's holder codes are
// lowercase, so nothing ever matched and the initial 0 was rendered verbatim; and any rule
// that simply takes the cheapest ticket lands on the free infant fare, which is not a price
// anyone can buy the activity for. So: cheapest ADULT fare, falling back to the cheapest
// non-zero fare for products that have no adult type at all (e.g. 'standard-price'), and
// nothing at all rather than a zero.
const price = computed(() => {
  let adult: {amount: number, formatted: string} | null = null
  let anyPaid: {amount: number, formatted: string} | null = null
  props.package?.time_slots?.forEach((slot: any) => {
    slot.ticket_types?.forEach((ticket: any) => {
      const amount = Number(ticket?.price?.amount)
      if (!Number.isFinite(amount) || amount <= 0) return
      const candidate = {amount, formatted: ticket.price.formatted}
      if (isAdultHolder(ticket.holder_type)) {
        if (!adult || amount < adult.amount) adult = candidate
      }
      if (!anyPaid || amount < anyPaid.amount) anyPaid = candidate
    })
  })
  const best: any = adult || anyPaid
  return best ? best.formatted : ''
})

const hasFreeCancellation = computed(() => {
  return property.value?.cancellation_policies?.some(
    (p: any) => p.refund_value == 100 && p.refund_type === 'percentage'
  )
})

const timeSlotsList = computed(() => {
  const list: any[] = []
  props.package.time_slots.forEach((item: any) => {
    list.push({
      label: item.time,
      value: item.id,
    })
  })
  slot.value = list.length ? list[0] : null
  return list
})


// Suppliers hand these back in their own order (Musement returns child before adult), so
// impose the order people expect. Anything unrecognised keeps its supplier position, between
// the named types and infants.
const HOLDER_RANK: Record<string, number> = {adult: 0, child: 1, senior: 2, infant: 4}
const sortTicketTypes = (ticketTypes: any[]) =>
  [...(ticketTypes || [])].sort((a: any, b: any) => {
    const rank = (t: any) => HOLDER_RANK[String(t?.holder_type).toLowerCase()] ?? 3
    return rank(a) - rank(b)
  })

const calculateTotalPrice = (ticketTypes: any) => {
  totalPrice.value = 0
  ticketTypes.forEach((item: any) => {
    totalPrice.value += selectedHolders.value[item.holder_type] * Number(item.price.amount)
  })
  totalPrice.value = totalPrice.value as any
}

const checkAvailability = async () => {
  const payload: any = {
    package_id: props.package.id,
    requested_tickets: []
  }
  for (let key in selectedHolders.value) {
    if (selectedHolders.value[key]) {
      payload.requested_tickets.push({
        holder_type: key,
        amount: selectedHolders.value[key]
      })
    }
  }
  const id = propertiesStore.property.id;
  const date = selectedActivityDate.value
  isPending.value = true
  noAvailablePackage.value = false
  await searchStore.searchActivitiesPackages({id, date, payload}).then((res: any) => {
    const ticketTypes = res?.packages?.[0]?.time_slots?.[0]?.ticket_types || []
    calculateTotalPrice(ticketTypes)
  }).catch(e => {
    const errors = e.data?.errors || []
    const NO_AVAILABLE_PACKAGES_CODE = 3101
    const noAvailablePackagesError = errors?.find((error: {code: number})=> error.code === NO_AVAILABLE_PACKAGES_CODE)
    if (noAvailablePackagesError) {
      noAvailablePackage.value = true
    }
  }).finally(() => {
    isPending.value = false
  })
}
const updatePackageSlot = (item: any) => {
  selectedTimeSlot.value = null
  selectedHolders.value = {}
  // Quantities are about to go back to zero, so the old total no longer describes anything.
  // Leaving it stale kept the summary line showing a price for an empty selection.
  totalPrice.value = 0

  if (item.value) {
    selectedTimeSlot.value = props.package.time_slots.find((slot: any) => slot.id === item.value)
  }

  if (selectedTimeSlot.value) {
    ticketsList.value = sortTicketTypes(selectedTimeSlot.value.ticket_types)
    ticketsList.value.forEach((ticket: any) => {
      selectedHolders.value[ticket.holder_type] = 0
    })
  }

}
const updateAvailability = (type: any, val: any, max: any) => {
  if ((selectedHolders.value[type] + val) >= 0) {
    if (val < 0) {
      selectedHolders.value[type] += val
    } else {
      if (max === null || max === undefined || selectedHolders.value[type] < max) {
        selectedHolders.value[type] += val
      }
    }
  }

  checkAvailability()
}

function setWithExpiry(key: any, value: any, ttl: any) {
  const now = new Date();
  // `item` is an object which contains the original value as well as the time when it's supposed to expire
  const item = {
    value: value,
    expiry: now.getTime() + ttl,
  };
  localStorage.setItem(key, JSON.stringify(item));
}

const bookNow = async () => {
  const availabilities: any[] = []
  Object.entries(selectedHolders.value).forEach(([key, value]) => {
    for (let i = 0; i < (value as any); i++) {
      availabilities.push({
        ticket_type_id: key,
        holder_type: key,
        pax: {}
      })
    }
  });

  const payload = {
    availabilities: availabilities,
    package_id: props.package.id,
    pickup_id: null,
    product_date: selectedActivityDate.value,
    product_id: propertiesStore.property.id,
    time_slot: selectedTimeSlot?.value?.time,
    type: "activity"
  }

  // The button is disabled in both these cases, but the 422 they produce is invisible: the
  // activity path fails schema validation before order-api's error-logging middleware, so a
  // rejected booking leaves no server-side trace at all. Fail here, with something to read.
  if (!availabilities.length) {
    bookingError.value = 'Please choose how many tickets you need.'
    return
  }
  if (!selectedActivityDate.value) {
    bookingError.value = 'Please choose a date for your activity.'
    return
  }

  isBooking.value = true
  bookingError.value = ''
  try {
    const response:any = await bookingStore.createActivityExpressOrder(payload);
    const { uid } = response;
    gtag('event', 'add_to_cart', {
      transaction_id: uid || '',
      currency: 'USD',
      value: +response.deposit?.amount,
      id: propertiesStore.property.id,
      name:propertiesStore.property.name,
      category: 'activity',
      affiliation: 'Tripening',
      items: response.items[0].availabilities.map((item: any) => {
        return {
          type: item.holder_title,
          price: item.price.amount,
        }
      })
    })
    const ONE_DAY = 1000 * 60 * 60 * 24;
    localStorage.removeItem('expressOrderUidExpiry');
    setWithExpiry('expressOrderUidExpiry', uid, ONE_DAY)
    await router.push(localePath('/checkout'))
  } catch (e: any) {
    // The button used to just silently re-enable: the store swallowed the error and returned
    // undefined, so this caught an unrelated TypeError from destructuring and logged it.
    console.error('[activity] booking failed', e)
    // order-api errors are {code, detail, meta} (tripening.aiohttp_utils format_errors) — there is
    // no `message` key, so reading it showed the generic line for every 422, including plain
    // "not available on this date". Legacy `message` kept as a second choice.
    const firstError = e?.data?.errors?.[0] || e?.response?._data?.errors?.[0]
    bookingError.value = firstError?.detail
      || firstError?.message
      || 'We could not start your booking. Please try again.'
  } finally {
    // Runs on the success path too, but the route change unmounts this component first.
    isBooking.value = false
  }
}

const openPackage = () => {
  propertiesStore.setSelectedPackageId(props.package.id)
}

const selectDateTime = (time: string, disabled:boolean) => {
  if (!disabled) {
    selectedDateTime.value = time
  }
}

const timeOfDayIs = (time:string) => {
  if (!time) return ''

  const timeToMoment = (t:string) => dayjs(t, 'HH:mm:ss')
  const dateTime = timeToMoment(time)
  const nEmS = timeToMoment('06:00:00')
  const mEaS = timeToMoment('12:00:00')
  const aEeS = timeToMoment('18:00:00')
  const eEnS = timeToMoment('23:59:59')

  if (dateTime.isSameOrAfter(nEmS) && dateTime.isBefore(mEaS)) return 'morning'
  if (dateTime.isSameOrAfter(mEaS) && dateTime.isBefore(aEeS)) return 'afternoon'
  if (dateTime.isSameOrAfter(aEeS) && dateTime.isBefore(eEnS)) return 'evening'
  return 'night'
}

onMounted(() => {
  availabilitiesList.value = props.package
  const times:any =  []
  props.package.time_slots.forEach((item:any) => {
    times.push({
      label: item.time,
      value: item.id,
    })
  })

  for (const item of times) {
    const partOfDay = timeOfDayIs(item.label)
    if ((groupedTimes as any)[partOfDay]) {
      if (!selectedDateTime.value) {
        selectedDateTime.value = partOfDay
      }
      (groupedTimes as any)[partOfDay].push(item)
    }
  }

  // With the time picker hidden there is nothing for the user to click, so the single slot
  // must be selected here or the ticket rows never appear.
  if (isTimeless.value) {
    if (times.length) updatePackageSlot(times[0])
    return
  }

  if ((groupedTimes as any)[selectedDateTime.value]?.length === 1) {
    const slot = (groupedTimes as any)[selectedDateTime.value][0]
    if (!slot) return
    updatePackageSlot(slot)
  }
})
</script>

<style lang="scss">
.activity-package {
  background: var(--white);
  padding: 16px;
  border-radius: 10px;
  margin-bottom: 16px;
  border: 2px solid var(--white);

  &:last-child {
    margin-bottom: 0;
  }

  &.selected {
    border: 2px solid var(--black);
  }

  &--head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;

    &-title {
      display: flex;
      align-items: center;
      gap: 12px;
    }
  }

  &--time-slot {
    margin-bottom: 12px;
    width: 182px;
  }

  &--title {
    font-size: 16px;
    font-weight: 600;
  }

  &--date {
    margin-bottom: 8px;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0;
    color: var(--black);
  }

  .availability-selector {
    max-width: 100%;
    background: var(--white);
    padding: 16px 0;
    border-top: 1px solid var(--grey-border);

    &.visitors-panel--property-block {
      width: 333px;
    }

    .availability-selector__item {
      display: flex;
      align-items: center;
      &:not(:last-child) {
        padding-bottom: 20px;
      }
      .availability-selector__item-info {
        flex: 1;
      }
      .availability-quantity {
        display: flex;
        align-items: center;

        .availability-quantity__value {
          min-width: 32px;
          text-align: center;
        }
        // NOT :deep() — this style block is not scoped, so `:deep(.p-button)` compiled to a
        // selector that matched nothing and the whole rule was dropped from the bundle. The
        // buttons then fell back to the PrimeVue default (padding 6px 10px, auto width) and
        // rendered as 35x27 ovals instead of 36px circles.
        .p-button {
          width: 36px !important;
          height: 36px !important;
          min-width: 36px !important;
          max-width: 36px !important;
          background-color: var(--blue-highlight) !important;
          border-color: var(--blue-highlight) !important;
          color: var(--blue) !important;
          margin: 4px;
          padding: 0 !important;
          display: flex !important;
          align-items: center;
          justify-content: center;
          border-radius: 50% !important;

          &:disabled {
            background-color: var(--light-grey) !important;
            border-color: var(--light-grey) !important;
            color: var(--grey) !important;
            opacity: 1 !important;
          }

          .icon {
            font-size: 16px;
            line-height: 1;
          }
        }
      }

      .ticket-price {
        font-size: 16px;
        font-weight: 600;
        color: var(--dark);
      }
    }
    .children-age-selector {
      border-top: 1px solid var(--grey-border);
      margin-top: 24px;
      padding-top: 24px;
      .children-age-selector__item {
        display: flex;
        align-items: center;

        &-value {
          border-width: 2px;
          border-color: var(--grey);
          &:focus, &:hover, &:focus-within {
            outline-color: var(--black);
            border-color: var(--black);
          }
        }
        $blackColorFilter: brightness(0) saturate(100%) invert(18%) sepia(0%) saturate(1189%) hue-rotate(154deg) brightness(92%) contrast(101%); //#292929

        &:not(:last-child) {
          padding-bottom: 20px;
        }
        :global(.p-dropdown) {
          width: 86px;
        }

        :global(.p-dropdown .p-dropdown-trigger svg) {
          transition: transform 0.3s;
        }

        :global(.p-dropdown:hover .p-dropdown-trigger svg) {
          filter: $blackColorFilter;
        }

        :global(.p-dropdown.p-overlay-open .p-dropdown-trigger svg) {
          transform: rotate(180deg);
          filter: $blackColorFilter;
        }

        :global(.p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight), :global(.p-dropdown-panel .p-dropdown-items .p-dropdown-item:hover) {
          background-color: var(--blue-highlight);
        }

      }
    }
  }

  // Busy state for the checkout hand-off. The old markup used `.icon-loading`, which is not a
  // glyph in the icomoon set — it rendered an empty span spinning on nothing, so pressing Book
  // now looked like it had done nothing at all. This spinner is pure CSS and needs no font.
  .btn-spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    margin-right: 8px;
    vertical-align: -2px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: btn-spinner-rotate 0.7s linear infinite;
  }

  @keyframes btn-spinner-rotate {
    to { transform: rotate(360deg); }
  }

  @media (prefers-reduced-motion: reduce) {
    .btn-spinner { animation-duration: 2.4s; }
  }

  .checkout-button {
    height: 48px;
    margin-top: 16px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    background-color: var(--blue) !important;
    border-color: var(--blue) !important;
    color: var(--white) !important;
    border-radius: 10px !important;
    width: auto;
    transition: background-color 0.2s, transform 0.1s;
    @media screen and (max-width: 768px) {
      width: 100%;
      height: 48px;
    }
    // Disabled buttons are dimmed; while busy the button is disabled but must still read as
    // active work rather than as an unavailable control.
    &.is-pending {
      opacity: 1 !important;
      cursor: progress;
    }

    &:not(:disabled):hover {
      background-color: var(--dark-blue) !important;
    }
    &:not(:disabled):active {
      transform: scale(0.98);
    }
    &:disabled {
      color: var(--dark-grey) !important;
      background-color: var(--grey-border) !important;
      border-color: var(--grey-border) !important;
      opacity: 1 !important;
    }

    .icon {
      font-size: 24px;
    }
  }

  .activity-package-footer {
    border-top: 1px solid var(--grey-border);

    .activity-package-error {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      margin: 12px 0 0 0;
      color: var(--red);
      display: flex;
      align-items: center;
      gap: 6px;
    }
  }

  .package-summery {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &--total {
      font-size: 16px;
      font-weight: 600;
      color: var(--dark);
    }

    &--tax {
      font-size: 12px;
      color: var(--dark-grey);

    }
  }

  .package-section {
    padding-left: 32px;
    padding-top: 16px;
  }

  .package-grouped-times {
    display: flex;
    align-items: center;
    border-bottom: 2px solid var(--grey-border);
    margin-bottom: 20px;

    &__item {
      padding: 0 16px 6px 16px;
      margin-bottom: -2px;
      border-bottom: 2px solid var(--grey-border);
      cursor: pointer;
      font-weight: 700;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0;
      text-align: center;

      &:hover {
        color: var(--blue);
      }

      &.active {
        color: var(--blue);
        border-bottom: 2px solid var(--blue);
      }

      &.disabled {
        color: var(--grey-border);
        cursor: default;
      }
    }
  }

  .package-times-list {
    display: flex;
    gap: 12px;
    margin-bottom: 20px;
    flex-wrap: wrap;

    &__item {
      border: 1px solid var(--grey-border);
      padding: 6px 16px;
      background-color: #F8F8F8;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0;
      cursor: pointer;
      border-radius: 16px;

      &.active {
        background-color: var(--blue-highlight);
        border-color: var(--blue-highlight);
      }
    }
  }

  .package-section-info {
    display: flex;
    flex-direction: column;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0;
    color: var(--black);
    padding: 0 0 16px 0;
    gap: 8px;

    .package-cancellation {
      display: flex;
      align-items: center;
      gap: 6px;
      color: #2e7d32;
      font-weight: 500;
      font-size: 13px;

      img {
        width: 16px;
        height: 16px;
      }
    }
  }
}
</style>
